import React, { useContext } from 'react';
import { translateBlockValue } from 'i18n';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import {
    BudgetProposalT,
    StatusesSettingsT,
    CellRendererParams,
    InscriptionWithMetadata,
} from 'routes/BudgetModule/config/types';
import {
    getBudgetProposalFromInscription,
    getInscriptionFieldValueForPhase,
    InscriptionGenericPhaseFieldT,
} from 'routes/BudgetModule/shared/entities/inscription';
import {
    getStatusesValuesForPhase,
    getStatusColor,
    isStatusVoted,
    getAllStatuses,
} from 'routes/BudgetModule/helpers/statusHelpers';
import { getFieldForPhase } from 'routes/BudgetModule/shared/phases/helpers';
import { StatusSettingsContext } from 'routes/BudgetModule/components/BudgetWithProvider/StatusSettingsContext';
import {
    StyStatus,
    StyTooltipMenuItem,
    StyStatusSelect,
} from 'routes/BudgetModule/components/shared/Status/StyStatus';
import { SelectChangeEvent } from '@mui/material';
import {
    BudgetPhases,
    SUPER_FINANCES_WRITER,
    SUPPORT,
} from 'business/constants';
import { UserRole } from 'business/definitions';
import { getClientBind } from 'store/APIClient';

const getStatusesAndRights = (
    data: InscriptionWithMetadata | undefined,
    budgetProposals: BudgetProposalT[],
    value: string,
    statusesSettings: StatusesSettingsT,
): {
    canEdit: boolean;
    displayStatuses: string[];
    budgetProposal: BudgetProposalT | null | undefined;
} => {
    if (!data)
        // in case we are in a grid grouped by status, data is null
        // so we want to get all the different possible statuses
        // to allow the select component to display it.
        return {
            canEdit: false,
            displayStatuses: getAllStatuses(statusesSettings),
            budgetProposal: undefined,
        };

    const { inscription } = data;

    const budgetProposal = getBudgetProposalFromInscription(
        inscription,
        budgetProposals,
    );
    const displayStatuses = getStatusesValuesForPhase(
        budgetProposal?.phase,
        statusesSettings,
    );
    const { canEdit } = inscription;

    return { canEdit, displayStatuses, budgetProposal };
};

export type StatusCellRendererParams = CellRendererParams<
    InscriptionWithMetadata,
    string
> & {
    budgetProposals: BudgetProposalT[];
    onInscriptionUpdate?: (arg1: {
        id: number | string;
        phase: BudgetPhases;
        data: unknown; // body of request
    }) => void;
    userRoles: UserRole[];
};

const mapping: Record<string, Record<string, UserRole[]>> = {
    ['ampmetropole']: {
        ['proposition_service_ready_arbitrage']: [SUPER_FINANCES_WRITER],
        ['voted_primitive']: [SUPPORT],
    },
};

const getDisabledValueFor = (
    status: string,
    userRoles: UserRole[],
    bind: string,
): boolean => {
    const rules = mapping[bind];
    if (rules === undefined) return false;

    const roles = rules[status];
    if (roles === undefined) return false;

    return !roles.some((role) => userRoles.includes(role));
};

export default function StatusCellRenderer({
    data,
    value,
    colDef,
    budgetProposals,
    setValue,
    onInscriptionUpdate,
    userRoles,
}: StatusCellRendererParams): React.ReactElement {
    const statusesSettings = useContext(StatusSettingsContext);
    const bind = getClientBind();

    if (!value) return <span />;

    const color = getStatusColor(value, statusesSettings);

    const { canEdit, displayStatuses, budgetProposal } = getStatusesAndRights(
        data,
        budgetProposals,
        value,
        statusesSettings,
    );

    return (
        <StyStatus color={color}>
            <StyStatusSelect
                selectedColor={color}
                canEdit={canEdit}
                value={value}
                variant="outlined"
                inputProps={{ disabled: !canEdit }}
                renderValue={(selectedValue: string) => (
                    <Tooltip title={translateBlockValue(value)}>
                        <StyTooltipMenuItem value={selectedValue}>
                            {translateBlockValue(selectedValue)}
                        </StyTooltipMenuItem>
                    </Tooltip>
                )}
                onChange={(event: SelectChangeEvent<string>) => {
                    const newValue = event.target.value;

                    if (!budgetProposal) return;
                    if (onInscriptionUpdate)
                        onInscriptionUpdate({
                            id: getInscriptionFieldValueForPhase(
                                // TODO: try to remove this non-null assertion
                                data!.inscription,
                                budgetProposal.phase,
                                'id',
                            ) as string | number,
                            phase: budgetProposal.phase,
                            data: {
                                [getFieldForPhase(
                                    budgetProposal.phase,
                                    colDef?.field as InscriptionGenericPhaseFieldT,
                                )!.split('.')[1]]: newValue,
                            },
                        });
                    setValue?.(newValue);
                }}
            >
                {displayStatuses
                    .filter(
                        (e) => !canEdit || !isStatusVoted(e, statusesSettings),
                    )
                    .map((status) => (
                        <MenuItem
                            key={status}
                            value={status}
                            disabled={getDisabledValueFor(
                                status,
                                userRoles,
                                bind,
                            )}
                        >
                            {translateBlockValue(status)}
                        </MenuItem>
                    ))}
            </StyStatusSelect>
        </StyStatus>
    );
}
